import { Link, useLocation } from "react-router-dom";
import { Users, Calendar, DollarSign, Settings, LayoutDashboard, Menu, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useActiveOrganization } from "@/hooks/useActiveOrganization";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useEffect, useMemo, useState } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import logo from "@/assets/logo.png";
import { useIsMobile } from "@/hooks/use-mobile";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { ProfileMenu } from "@/components/profile/ProfileMenu";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({
  children
}: LayoutProps) {
  const location = useLocation();
  const { signOut, user } = useAuth();
  const { isAdmin, isSecretary, isDoctor, currentUserRoles } = useUserRoles();
  const { organizations } = useOrganizations();
  const { activeOrganizationId, setActiveOrganization } = useActiveOrganization();
  const { role } = useUserRole();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Buscar perfil do usuário
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });
  
  // Buscar organização ativa com logo
  const activeOrganization = useMemo(() => 
    organizations.find(org => org.id === activeOrganizationId),
    [organizations, activeOrganizationId]
  );
  
  // Auto-selecionar primeira organização se não houver nenhuma selecionada
  // OU se a organização ativa não estiver mais na lista de organizações acessíveis
  useEffect(() => {
    if (organizations.length > 0) {
      const orgExists = organizations.some(org => org.id === activeOrganizationId);
      if (!activeOrganizationId || !orgExists) {
        setActiveOrganization(organizations[0].id);
      }
    } else if (activeOrganizationId) {
      // Se não há organizações disponíveis mas tem ID salvo, limpar
      setActiveOrganization(null);
    }
  }, [organizations, activeOrganizationId, setActiveOrganization]);
  
  const getRoleLabel = () => {
    if (role === 'admin') return "Admin";
    if (role === 'doctor') return "Médico";
    if (role === 'secretary') return "Secretária";
    return "Usuário";
  };

  const getRoleBadgeColor = () => {
    if (role === 'admin') return "bg-purple-500/20 text-purple-300 border-purple-400/30";
    if (role === 'doctor') return "bg-blue-500/20 text-blue-300 border-blue-400/30";
    if (role === 'secretary') return "bg-green-500/20 text-green-300 border-green-400/30";
    return "bg-gray-500/20 text-gray-300 border-gray-400/30";
  };
  
  const navigation = [{
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard
  }, {
    name: "Pacientes",
    href: "/patients",
    icon: Users
  }, {
    name: "Agenda",
    href: "/schedule",
    icon: Calendar
  }, {
    name: "Financeiro",
    href: "/finance",
    icon: DollarSign
  }, {
    name: "Estoque",
    href: "/stock",
    icon: Package
  }, {
    name: "Configurações",
    href: "/settings",
    icon: Settings
  }];
  
  const SidebarContent = () => (
    <>
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3">
          <img 
            src={logo} 
            alt="Logo" 
            className="h-10 w-10 object-contain flex-shrink-0" 
          />
          {organizations.length > 0 ? (
            <div className="flex-1 min-w-0">
              <Select
                value={activeOrganizationId || ""}
                onValueChange={setActiveOrganization}
              >
                <SelectTrigger className={`w-full border-sidebar-border/30 bg-white/10 text-sidebar-foreground ${!activeOrganizationId ? 'border-orange-500' : ''}`}>
                  <SelectValue placeholder="Selecione uma empresa" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!activeOrganizationId && (
                <p className="text-xs text-orange-500 mt-1">
                  ⚠️ Selecione uma empresa
                </p>
              )}
            </div>
          ) : (
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-sidebar-foreground truncate">
                Orion
              </h1>
              <p className="text-xs text-sidebar-foreground/60">Gestão Clínica</p>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-1">
        {navigation.map(item => {
          const isActive = location.pathname === item.href;
          return <Link 
            key={item.name} 
            to={item.href} 
            onClick={() => setSidebarOpen(false)}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
              isActive 
                ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm" 
                : "text-sidebar-foreground/70 hover:bg-white/10 hover:text-sidebar-foreground"
            )}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {item.name}
          </Link>;
        })}
      </nav>

      <div className="p-3 border-t border-sidebar-border/30">
        <ProfileMenu
          userEmail={user?.email}
          userName={profile?.full_name || undefined}
          avatarUrl={profile?.avatar_url || undefined}
          roleLabel={getRoleLabel()}
          roleBadgeColor={getRoleBadgeColor()}
          onSignOut={signOut}
        />
      </div>
    </>
  );

  return <div className="flex min-h-screen bg-background">
      {/* Mobile Header */}
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-sidebar border-b border-sidebar-border/40 flex items-center justify-between px-4">
          <div className="flex items-center">
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-sidebar-foreground">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 bg-sidebar [&>button]:text-sidebar-foreground">
                <SidebarContent />
              </SheetContent>
            </Sheet>
            <h1 className="ml-4 text-lg font-semibold text-sidebar-foreground truncate">
              {activeOrganization?.name || 'Orion'}
            </h1>
          </div>
          <NotificationBell />
        </header>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside className="w-64 h-screen sticky top-0 bg-sidebar border-r border-sidebar-border/50 flex flex-col overflow-y-auto">
          <SidebarContent />
        </aside>
      )}

      {/* Main Content */}
      <main className={cn("flex-1 overflow-auto", isMobile && "pt-14")}>
      {/* Desktop Notification Bell */}
      {!isMobile && (
        <div className="fixed top-4 right-6 z-40">
          <div className="bg-white rounded-full shadow-md border border-border">
            <NotificationBell variant="light" />
          </div>
        </div>
      )}
        {children}
      </main>
    </div>;
}